"use client";
import React, { useEffect, useState, useRef, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { BlogPost } from "@/types/blog";
import SocialMedia from "./SocialMedia";
import PostHeading from "./PostHeading";
import LiveTypingSummary from "./LiveTypingSummary";
import CommonButton from "@/components/common/CommonButton";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import moment from "moment";
import parse from "html-react-parser";
import postHeroBgImage from "@/assets/images/blog/blog-post-hero-bg.svg";
// import aiRobotImage from "@/assets/images/blog/ai-robot.webp";
import aiStarIcon from "@/assets/images/blog/ai-robot.webp";

interface PostPageProps {
  postData: BlogPost;
  detailCatId: string;
  host: string;
  ip: string;
}

function Conclusion({ conclusion }: { conclusion: string }) {
  return parse(conclusion);
}

function Summary({ summary }: { summary: string }) {
  return parse(summary);
}

export function useScrollSpy(ids: string[], options: IntersectionObserverInit) {
  const [activeId, setActiveId] = React.useState<string>();
  const observer = useRef<IntersectionObserver | null>(null);

  React.useEffect(() => {
    const elements = ids.map((id) => document.getElementById(id));
    observer.current?.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry?.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, options);
    elements.forEach((el) => {
      if (el) {
        observer.current?.observe(el);
      }
    });
    return () => observer.current?.disconnect();
  }, [ids, options]);
  return activeId;
}

const PostPage: React.FC<PostPageProps> = ({
  postData,
  detailCatId,
  host,
  ip,
}) => {
  const elementRef = useRef<HTMLUListElement>(null);
  const [like, setLike] = useState(false);
  const [heart, setHeart] = useState(false);
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);
  const [showRobotCenter, setShowRobotCenter] = useState(false);

  const getData = useCallback(async () => {
    try {
      const likedata = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/posts/${detailCatId}/?ip=${ip}`,
      );
      const data = await likedata.json();
      setLike(data?.data?.post_liked?.liked || false);
      setHeart(data?.data?.post_liked?.favourite || false);
    } catch (error) {
      console.error("Error fetching like data:", error);
    }
  }, [detailCatId, ip]);

  const activeId = useScrollSpy(
    postData?.table_of_content
      ?.map(({ id }) =>
        id.includes("contentid-") || id.includes("contenth3-") ? id : null,
      )
      .filter((id): id is string => Boolean(id)) || [],
    { rootMargin: "0% 0% -80% 0%" },
  );

  useEffect(() => {
    if (activeId && activeId.startsWith("contentid-"))
      setOpenSectionId(activeId);
  }, [activeId]);

  const handleScroll = useCallback(() => {
    const rmelement1 = document.querySelector(".cls-footer");
    const elementstick = document.querySelector("#heeelo");
    const element = document.querySelector(".content-shown");
    const stickyClass = "sticky12";
    if (rmelement1 && elementstick && element) {
      const { top, height } = element.getBoundingClientRect();
      const rmtop = rmelement1.getBoundingClientRect().y;
      const ulheight = elementstick.getBoundingClientRect().height;
      const ultop = elementstick.getBoundingClientRect().top;
      if (top < 90 && rmtop > ulheight + 90) {
        elementstick.classList.add(stickyClass);
      } else {
        elementstick.classList.remove(stickyClass);
      }
    }
  }, []);

  useEffect(() => {
    const scrollHandler = () => handleScroll();
    window.addEventListener("scroll", scrollHandler);
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, [handleScroll]);

  const nestedTableOfContent: Array<{
    id: string;
    name: string;
    subItems: Array<{ id: string; name: string }>;
  }> = [];

  postData?.table_of_content?.forEach((item: { id: string; name: string }) => {
    if (item.id.startsWith("contentid-")) {
      nestedTableOfContent.push({ ...item, subItems: [] });
    } else if (item.id.startsWith("contenth3-")) {
      const last = nestedTableOfContent[nestedTableOfContent.length - 1];
      if (last) last.subItems.push(item);
    }
  });

  // Check if there are any h2 or h3 headings
  const hasTableOfContent = nestedTableOfContent.length > 0;

  return (
    <>
      <div className="relative mt-16 md:mt-12 lg:mt-16">
        <div className="relative overflow-hidden py-4 md:py-8">
          <div
            className="absolute inset-0 z-0 h-full w-full opacity-15"
            style={{
              backgroundImage: `url(${postHeroBgImage.src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          />
          <div className="container relative z-10 mx-auto px-8 py-4">
            <div className="ipad-mr-blog-1 flex flex-col items-center lg:flex-row lg:gap-8 xl:justify-between xl:gap-12">
              <div className="main-blog-details-ipad md:mb-10 lg:mb-0">
                <div className="flex items-center justify-center overflow-hidden rounded-2xl">
                  <Image
                    src={postData?.f_image_url || "/placeholder-image.jpg"}
                    alt={
                      postData?.alt_title === null
                        ? postData?.meta_title || postData?.title
                        : postData?.alt_title || postData?.title
                    }
                    width={600}
                    height={600}
                    className="rounded-2xl object-cover pb-6 lg:pb-0 lg:shadow-2xl"
                    priority
                    placeholder="blur"
                    blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO6aDPwAAAAASUVORK5CYII="
                  />
                </div>
              </div>
              <div className="main-blog-details-ipad block-details gap-2">
                <PostHeading
                  title={postData?.title || ""}
                  colorCode={postData?.subcategory_detail?.color_code}
                  subcatTitle={postData?.subcategory_detail?.title}
                  detailCatId={detailCatId}
                  getData={getData}
                  setLike={setLike}
                  like={like}
                  heart={heart}
                  setHeart={setHeart}
                  ip={ip}
                />
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-8 lg:gap-4">
                    <p className="flex-shrink-0 text-[16px] font-medium text-[#202020]">
                      {moment(postData?.publish_date).format("MMM DD, YYYY")}
                    </p>
                    {postData?.creator && postData?.category_detail?.title !== "News" && <p className="text-[16px] font-medium text-[#202020]">-</p>}
                    {postData?.creator && postData?.category_detail?.title !== "News" && <p className="flex-shrink-0 text-base font-medium text-[#202020]">
                      By {postData?.creator}
                    </p>}
                  </div>
                </div>
                <div className="flex flex-col gap-4 pt-4 md:flex-row md:items-center md:justify-between md:gap-0 xl:pt-6">
                  <CommonButton
                    text="Get Started with Kroolo AI"
                    isSecondaryBg
                    textWeightMedium
                    textSizeBtnClass="text-[14px] h-[40px]"
                    url="https://cta-service-cms2.hubspot.com/web-interactives/public/v1/track/click?encryptedPayload=AVxigLJuAq9xeWhJk8yPHtahUTu8pZzCPduO5mg3ZLtyb74cksLAjwjeE4dMs1xIXFDPoa%2F5AmxlUfiDsxhConmhC3G35v%2BABCZf1uGsmGuCYlyMekidCu2IgHGn7p4Xve3hPNMTgCqU5OXzeB9CJhy%2FpCoTKXi6%2F8tHmLwYY%2B0%2FugW5&amp;portalId=44494863"
                  />
                  <div className="items-baseline justify-between lg:flex">
                    <SocialMedia
                      title={postData?.title || ""}
                      slug={postData?.slug || ""}
                      host={host}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white">
          <div className="container mx-auto px-8 py-16">
            <div
              className={`flex gap-4 md:gap-12 ${hasTableOfContent ? "" : "justify-center"}`}
            >
              <div
                className={`main-bd-wpr w-full gap-4 pb-10 sm:pb-0 ${hasTableOfContent ? "lg:w-[75%]" : "w-full"}`}
              >
                {postData?.summary_description && (
                  <div className="content-shown-summary relative mb-8 gap-2 overflow-hidden rounded-lg p-2 pt-2 md:p-6 md:pt-5">
                    <div
                      className="absolute inset-0 z-0 h-full w-full opacity-15"
                      style={{
                        backgroundImage: `url(${postHeroBgImage.src})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                    <p
                      className="leading-1 relative z-10 flex flex-nowrap items-center gap-2 !text-[20px] text-xl !font-semibold text-black"
                      id="summary"
                    >
                      <Image
                        src={aiStarIcon}
                        alt="AI highlight - Kroolo blog"
                        width={28}
                        height={28}
                        className="inline-block h-6 w-6 md:h-8 md:w-8"
                      />
                      AI Summary By Kroolo
                    </p>
                    <div className="relative z-10 will-change-auto">
                      <LiveTypingSummary
                        htmlContent={postData.summary_description}
                      />
                    </div>
                  </div>
                )}
                <div
                  className="content-shown text-base text-black"
                  dangerouslySetInnerHTML={{
                    __html:
                      postData?.content ||
                      postData?.description ||
                      "Content coming soon...",
                  }}
                ></div>
                {postData?.conclusion && (
                  <div className="content-shown-conclusion mt-8 gap-2 rounded-lg bg-gray-100 p-6 pt-5">
                    <p
                      className="leading-6-mr-5 !text-[33px] text-xl !font-semibold text-black"
                      id="conclusion"
                    >
                      Conclusion
                    </p>
                    <div>{parse(postData?.conclusion)}</div>
                  </div>
                )}
                <div className="tag-details mt-8">
                  {postData?.tags_detail &&
                    postData.tags_detail.length !== 0 && (
                      <>
                        <p className="mb-5 text-2xl font-medium text-black">
                          Tags
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {postData?.tags_detail?.map((item, index) => (
                            <p
                              key={index.toString()}
                              className="rounded-full px-3 py-1 font-medium text-black"
                              style={{ backgroundColor: "rgb(251, 196, 171)" }}
                            >
                              {item?.name}
                            </p>
                          ))}
                        </div>
                      </>
                    )}
                </div>
              </div>
              {hasTableOfContent && (
                <div className="main-blmain blog-detail-table sticky top-20 hidden h-[fit-content] overflow-y-auto px-0 lg:block lg:h-[100vh] lg:w-[25%]">
                  <ul
                    ref={elementRef}
                    className="Detail--Page--list rounded-md border border-gray-300 p-4 pb-10 text-[#6A707C] sm:pb-4"
                    style={{
                      background:
                        "linear-gradient(180deg, #ebf8f6, #e5e8f2, #f7e6e6)",
                    }}
                    id="heeelo"
                  >
                    <li>
                      <p className="mb-5 text-[22px] font-semibold text-black">
                        Table of contents
                      </p>
                    </li>
                    {postData?.summary_description && (
                      <li className="mb-2 px-2">
                        <Link
                          href="#summary"
                          className={activeId === `summary` ? "tblactive" : ""}
                        >
                          <p>Summary</p>
                        </Link>
                      </li>
                    )}
                    {nestedTableOfContent.map((item, index) => (
                      <li key={index}>
                        <div className="flex items-center justify-between">
                          <Link href={`#${item.id}`} className="w-full">
                            <p
                              className={`flex flex-row items-center rounded-lg px-2 py-1.5 text-[15px] ${[openSectionId, activeId].includes(item.id)
                                  ? "tblactive"
                                  : "font-normal"
                                }`}
                            >
                              <span title={item.name}>{item.name}</span>
                              {item.subItems.length > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenSectionId((prevId) =>
                                      prevId === item.id ? null : item.id,
                                    );
                                  }}
                                  className="-mr-1 ml-auto shrink-0 rounded p-1 text-lg hover:!bg-[#d0d0d0]"
                                >
                                  {openSectionId === item.id ? (
                                    <FiChevronUp />
                                  ) : (
                                    <FiChevronDown />
                                  )}
                                </button>
                              )}
                            </p>
                          </Link>
                        </div>
                        {item.subItems.length > 0 &&
                          openSectionId === item.id && (
                            <ul className="ml-2 mt-2">
                              {item.subItems.map(
                                (
                                  subItem: { id: string; name: string },
                                  subIndex: number,
                                ) => (
                                  <li key={subIndex}>
                                    <Link href={`#${subItem.id}`}>
                                      <p
                                        className={`flex flex-row items-center rounded-lg px-2 py-1.5 text-[13px] ${[activeId].includes(subItem.id)
                                            ? "tblactive"
                                            : "font-normal"
                                          }`}
                                      >
                                        <span title={subItem.name}>
                                          {subItem.name}
                                        </span>
                                      </p>
                                    </Link>
                                  </li>
                                ),
                              )}
                            </ul>
                          )}
                      </li>
                    ))}
                    {postData?.conclusion && (
                      <li className="mb-4 mt-2 p-2">
                        <Link
                          href="#conclusion"
                          className={
                            activeId === `conclusion` ? "tblactive" : ""
                          }
                        >
                          <p>Conclusion</p>
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PostPage;
